/**
 * ============================================================================
 * ANALYTICS OVERVIEW COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Overview dashboard with key metrics
 */

import React, { useEffect, useState } from 'react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import AnalyticsCard from './AnalyticsCard';
import Card from '../common/Card';
import Spinner from '../common/Spinner';
import Button from '../common/Button';
import Select from '../common/Select';

/**
 * Analytics Overview Component
 * @returns {JSX.Element}
 */
const AnalyticsOverview = () => {
  const { loadDashboardAnalytics, dashboardAnalytics, loading } = useAnalytics();
  const { showError } = useToast();
  const { formatCurrency, formatNumber, language } = useI18n();
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    const loadData = async () => {
      try {
        await loadDashboardAnalytics({ timeRange });
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement' : 'Failed to load')
        );
      }
    };

    loadData();
  }, [timeRange, loadDashboardAnalytics, showError, language]);

  if (loading && !dashboardAnalytics) {
    return (
      <Card variant="glass" className="p-12">
        <div className="flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  const metrics = dashboardAnalytics?.metrics || {};
  const transactions = metrics.transactions || {};
  const financial = metrics.financial || {};
  const disputes = metrics.disputes || {};

  return (
    <div className="space-y-6">
      {/* Header with Time Range */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black text-gray-900 dark:text-white">
          📊 {language === 'fr' ? 'Vue d\'ensemble' : 'Overview'}
        </h2>
        <Select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          options={[
            { value: '7d', label: language === 'fr' ? '7 jours' : '7 Days' },
            { value: '30d', label: language === 'fr' ? '30 jours' : '30 Days' },
            { value: '90d', label: language === 'fr' ? '90 jours' : '90 Days' },
            { value: '1y', label: language === 'fr' ? '1 an' : '1 Year' },
          ]}
          className="w-40"
        />
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <AnalyticsCard
          title={language === 'fr' ? 'Transactions Total' : 'Total Transactions'}
          value={formatNumber(transactions.total || 0)}
          icon="📦"
          color="from-blue-500 to-cyan-500"
          trend={transactions.trend}
          trendValue={transactions.trendValue}
          description={language === 'fr' ? 'Toutes vos transactions' : 'All your transactions'}
        />
        <AnalyticsCard
          title={language === 'fr' ? 'Montant Total' : 'Total Amount'}
          value={formatCurrency(transactions.totalAmount ?? transactions.totalValue ?? 0, 'XAF')}
          icon="💰"
          color="from-green-500 to-emerald-500"
          trend={transactions.amountTrend}
          trendValue={transactions.amountTrendValue}
          description={language === 'fr' ? 'Valeur totale' : 'Total value'}
        />
        <AnalyticsCard
          title={language === 'fr' ? 'Taux de Réussite' : 'Success Rate'}
          value={`${Number(transactions.successRate ?? 0).toFixed(1)}%`}
          icon="✅"
          color="from-green-500 to-teal-500"
          trend={transactions.rateTrend}
          trendValue={transactions.rateTrendValue}
          description={language === 'fr' ? 'Transactions réussies' : 'Successful transactions'}
        />
        <AnalyticsCard
          title={language === 'fr' ? 'Litiges' : 'Disputes'}
          value={formatNumber(disputes.total || 0)}
          icon="⚠️"
          color="from-red-500 to-pink-500"
          trend={disputes.trend}
          trendValue={disputes.trendValue}
          description={language === 'fr' ? 'Litiges en cours' : 'Active disputes'}
        />
      </div>

      {/* Financial Summary */}
      {(financial.totalEarned || financial.totalSpent) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AnalyticsCard
            title={language === 'fr' ? 'Total Gagné' : 'Total Earned'}
            value={formatCurrency(financial.totalEarned || 0, 'XAF')}
            icon="📈"
            color="from-green-500 to-emerald-500"
            trend={financial.earnedTrend}
            trendValue={financial.earnedTrendValue}
          />
          <AnalyticsCard
            title={language === 'fr' ? 'Total Dépensé' : 'Total Spent'}
            value={formatCurrency(financial.totalSpent || 0, 'XAF')}
            icon="💸"
            color="from-purple-500 to-pink-500"
            trend={financial.spentTrend}
            trendValue={financial.spentTrendValue}
          />
        </div>
      )}
    </div>
  );
};

export default AnalyticsOverview;
